/**
 * Delete Tenant Modal Component
 *
 * Confirmation modal for tenant deletion.
 *
 * @fileoverview Tenant deletion confirmation modal
 */

import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import type { AdminTenant } from '../../types/admin';

interface DeleteTenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenant: AdminTenant | null;
  loading: boolean;
  onConfirm: () => void;
}

/**
 * Delete Tenant Modal Component
 *
 * Shows confirmation dialog before deleting a tenant.
 */
export const DeleteTenantModal: React.FC<DeleteTenantModalProps> = ({
  isOpen,
  onClose,
  tenant,
  loading,
  onConfirm,
}) => {
  if (!tenant) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Delete Tenant</ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              Are you sure you want to delete{' '}
              <Text as="span" fontWeight="bold">
                {tenant.name}
              </Text>
              ?
            </Text>

            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                This action cannot be undone. All tenant members will be removed
                and any associated data will be permanently deleted.
              </AlertDescription>
            </Alert>

            {tenant._count?.members && tenant._count.members > 0 && (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  This tenant has {tenant._count.members} member(s) who will lose
                  access.
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={loading}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            onClick={onConfirm}
            isLoading={loading}
            loadingText="Deleting..."
          >
            Delete Tenant
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeleteTenantModal;
